import React from 'react';
import { MaintenanceStatus } from '@/types/common';
import { MaintenanceStatusHistoryEntry } from '@/services/api/maintenances';

interface MaintenanceStatusTimelineProps {
  statusHistory: MaintenanceStatusHistoryEntry[];
  estimatedCost?: number;
}

const formatTimelineDateTime = (value: string) => {
  if (!value) {
    return { date: '-', time: '' };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const parts = value.split(' ');
    return {
      date: parts.slice(0, 3).join(' ') || value,
      time: parts.slice(3).join(' '),
    };
  }

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);

  return { date, time };
};

const getStatusPillClasses = (status?: string) => {
  switch (status) {
    case MaintenanceStatus.OPEN:
      return 'bg-blue-100 text-[#335CFF]';
    case MaintenanceStatus.IN_PROGRESS:
      return 'bg-yellow-100 text-yellow-700';
    case MaintenanceStatus.COMPLETED:
      return 'bg-green-100 text-[#1FC16B]';
    case MaintenanceStatus.PENDING_APPROVAL_COMPLETION:
    case MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP:
    case MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP:
    case MaintenanceStatus.PENDING_APPROVAL:
      return 'bg-[#EFEAF7] text-[#5E2AAC]';
    case MaintenanceStatus.CLOSED:
      return 'bg-gray-100 text-gray-600';
    case MaintenanceStatus.TEMP_SWAP:
      return 'bg-[#FFF3EB] text-[#FA7319]';
    case MaintenanceStatus.PERM_SWAP:
      return 'bg-[#FB3748]/10 text-[#FB3748]';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatStatusLabel = (status?: string) => {
  if (!status) return 'UNKNOWN';
  const LABEL_MAP: Partial<Record<MaintenanceStatus, string>> = {
    [MaintenanceStatus.PERM_SWAP]: 'PERMANENT SWAP',
    [MaintenanceStatus.TEMP_SWAP]: 'TEMPORARY SWAP',
    [MaintenanceStatus.PENDING_APPROVAL_COMPLETION]: 'PENDING APPROVAL',
    [MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP]: 'PENDING APPROVAL',
    [MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP]: 'PENDING APPROVAL',
  };
  return LABEL_MAP[status as MaintenanceStatus] ?? status.replace(/_/g, ' ');
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '';
  return `Rp. ${value.toLocaleString('id-ID')}`;
};

// Formats to DD/MM/YYYY HH:mm (local time, 24-hour, no seconds)
const formatDateTimeDDMMYYYYHHmm = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(parsed)
    .replace(/\./g, ':');
};

const getActionText = (entry: MaintenanceStatusHistoryEntry) => {
  if (entry.metadata?.decision === 'REJECT') {
    return 'Rejected';
  }

  if (
    entry.fromStatus === entry.toStatus &&
    entry.toStatus === MaintenanceStatus.OPEN
  ) {
    return 'Case created';
  }

  switch (entry.toStatus) {
    case MaintenanceStatus.IN_PROGRESS:
      if (entry.metadata?.decision === 'REJECT') {
        return 'Rejected';
      }
      return 'Started repair process at OEM Center';
    case MaintenanceStatus.PENDING_APPROVAL_COMPLETION:
    case MaintenanceStatus.PENDING_APPROVAL_TEMP_SWAP:
    case MaintenanceStatus.PENDING_APPROVAL_PERM_SWAP:
    case MaintenanceStatus.PENDING_APPROVAL:
      return "Waiting for Manager's Approval";
    case MaintenanceStatus.CLOSED:
      return 'Case closed';
    case MaintenanceStatus.TEMP_SWAP:
      return 'Approve Temporary Swap';
    case MaintenanceStatus.PERM_SWAP:
      return 'Approve Permanent Swap';
    default:
      return '';
  }
};

// ─── Reusable meta row ────────────────────────────────────────────────────────

const MetaRow: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
      {icon}
    </span>
    <div className="flex-1 min-w-0 mt-1">{children}</div>
  </div>
);

const UserIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={`h-3 w-3 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const MaintenanceStatusTimeline: React.FC<MaintenanceStatusTimelineProps> = ({
  statusHistory,
  estimatedCost,
}) => {
  return (
    <div className="w-full max-w-full overflow-hidden sticky top-5 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-5 py-4">
        <div className="text-base font-semibold text-gray-900">
          Status Timeline
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-5 py-2">
        <div className="text-xs font-medium tracking-wide text-gray-400">
          Maintenance Progress
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="space-y-4">
          {statusHistory.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              No status history available yet.
            </div>
          )}

          {statusHistory.map((entry, index) => {
            const { date, time } = formatTimelineDateTime(entry.createdAt);
            const metadata = entry.metadata;

            const isCurrent = index === 0;
            const detailTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const detailMutedClass = isCurrent ? 'text-black' : 'text-gray-400';
            const iconClass = isCurrent ? 'text-black' : 'text-gray-400';
            const timeTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const statusPill = getStatusPillClasses(entry.toStatus as string);
            const statusLabel = formatStatusLabel(entry.toStatus as string);
            const actionText = getActionText(entry);

            return (
              <div key={entry.ID} className="flex items-start gap-3">
                {/* ── Timeline bullet & vertical connector ── */}
                <div className="flex flex-col items-center self-stretch">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      isCurrent
                        ? 'border-purple-400 text-purple-600'
                        : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {isCurrent ? (
                      <div className="h-2.5 w-2.5 rounded-full bg-purple-600" />
                    ) : (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  {index !== statusHistory.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200" />
                  )}
                </div>

                {/* ── Entry body ── */}
                <div className="min-w-0 flex-1 pb-4">
                  {/* Header: date / action text / time */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {date}
                        </div>
                      </div>

                      {/* Status pill + optional info icon */}
                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusPill}`}
                      >
                        {statusLabel}
                        {actionText && (
                          <span className="group relative flex items-center">
                            <i className="ri-information-line text-[10px]" />
                            <span className="pointer-events-none absolute left-1/2 top-[-38px] hidden w-max max-w-[220px] -translate-x-1/2 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-500 shadow-sm group-hover:block">
                              {actionText}
                            </span>
                          </span>
                        )}
                      </span>
                    </div>

                    <div className={`text-xs flex-shrink-0 ${timeTextClass}`}>
                      {time}
                    </div>
                  </div>

                  {/* ── Metadata rows ── */}
                  <div
                    className={`mt-2 space-y-1.5 text-xs ${detailTextClass}`}
                  >
                    {/* Created by */}
                    <MetaRow icon={<UserIcon className={iconClass} />}>
                      <span className="break-all">{entry.createdBy}</span>
                    </MetaRow>

                    {/* PENDING_APPROVAL_COMPLETION / COMPLETED: Permanent Swap Date & Time */}
                    {metadata?.permSwapReturnAt && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-calendar-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>
                          Permanent Swap Date &amp; Time:
                        </div>
                        <div className={`mt-0.5 ${detailTextClass}`}>
                          {formatDateTimeDDMMYYYYHHmm(
                            metadata.permSwapReturnAt
                          )}
                        </div>
                      </MetaRow>
                    )}

                    {/* COMPLETED: Work completed (string from API) */}
                    {metadata?.workCompleted && (
                      <MetaRow
                        icon={
                          <i className={`ri-tools-line text-xs ${iconClass}`} />
                        }
                      >
                        <div className={detailMutedClass}>Work Completed:</div>
                        <div
                          className={`mt-0.5 break-words whitespace-pre-line ${detailTextClass}`}
                        >
                          {metadata.workCompleted}
                        </div>
                      </MetaRow>
                    )}

                    {/* COMPLETED: Actual completion date */}
                    {metadata?.actualCompletionAt && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-calendar-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>
                          Actual Completion Date:
                        </div>
                        <div className={`mt-0.5 ${detailTextClass}`}>
                          {formatDateTimeDDMMYYYYHHmm(
                            metadata.actualCompletionAt
                          )}
                        </div>
                      </MetaRow>
                    )}

                    {/* COMPLETED: Completion photo */}
                    {metadata?.completionImageURL && (
                      <MetaRow
                        icon={
                          <i className={`ri-image-line text-xs ${iconClass}`} />
                        }
                      >
                        <div className={detailMutedClass}>Photo:</div>
                        <a
                          href={metadata.completionImageURL}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline break-all"
                        >
                          {metadata.completionImageURL}
                        </a>
                      </MetaRow>
                    )}

                    {/* COMPLETED: Completion notes */}
                    {metadata?.completionNotes && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-file-text-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Notes:</div>
                        <div
                          className={`mt-0.5 break-words whitespace-pre-line ${detailTextClass}`}
                        >
                          {metadata.completionNotes}
                        </div>
                      </MetaRow>
                    )}

                    {/* COMPLETED: Actual cost */}
                    {metadata?.actualCost !== undefined &&
                      metadata?.actualCost !== null && (
                        <MetaRow
                          icon={
                            <i
                              className={`ri-money-dollar-circle-line text-xs ${iconClass}`}
                            />
                          }
                        >
                          <span className={detailTextClass}>
                            <span className={detailMutedClass}>
                              Actual Final Cost:{' '}
                            </span>
                            {/* If cost is same as estimate, display the current estimatedCost if available, otherwise fallback to metadata.actualCost */}
                            {formatCurrency(
                              metadata.isCostSameAsEstimate &&
                                estimatedCost !== undefined
                                ? estimatedCost
                                : metadata.actualCost
                            )}
                            {metadata.isCostSameAsEstimate && (
                              <span className="ml-1 text-gray-400">
                                (Same with Estimated Cost)
                              </span>
                            )}
                            {!metadata.isCostSameAsEstimate &&
                              metadata.costVariance === 'HIGHER' && (
                                <span className={`ml-1 ${detailMutedClass}`}>
                                  (Higher than Estimated)
                                </span>
                              )}
                            {!metadata.isCostSameAsEstimate &&
                              metadata.costVariance === 'LOWER' && (
                                <span className={`ml-1 ${detailMutedClass}`}>
                                  (Lower than Estimated)
                                </span>
                              )}
                          </span>
                        </MetaRow>
                      )}

                    {/* PENDING_APPROVAL_COMPLETION / COMPLETED: Cost variance reason */}
                    {metadata?.costVarianceReason && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-file-text-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <span className={detailTextClass}>
                          <span className={detailMutedClass}>Reason: </span>
                          {metadata.costVarianceReason}
                        </span>
                      </MetaRow>
                    )}

                    {/* PERM SWAP / IN PROGRESS: Decision (hide for TEMP SWAP & PERM SWAP & COMPLETED & REJECT cases) */}
                    {metadata?.decision &&
                      metadata.decision !== 'REJECT' &&
                      entry.toStatus !== MaintenanceStatus.TEMP_SWAP &&
                      entry.toStatus !== MaintenanceStatus.PERM_SWAP &&
                      entry.toStatus !== MaintenanceStatus.COMPLETED && (
                        <MetaRow
                          icon={
                            <i
                              className={`ri-checkbox-circle-line text-xs ${metadata.decision === 'APPROVE' ? 'text-green-500' : 'text-red-500'}`}
                            />
                          }
                        >
                          <span className={detailMutedClass}>Decision: </span>
                          <span
                            className={`font-medium ${metadata.decision === 'APPROVE' ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {metadata.decision}
                          </span>
                        </MetaRow>
                      )}

                    {/* PERM SWAP: Decision Notes */}
                    {metadata?.decisionNotes && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-file-text-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>
                          {entry.toStatus === MaintenanceStatus.PERM_SWAP ||
                          entry.toStatus === MaintenanceStatus.TEMP_SWAP
                            ? 'Notes:'
                            : 'Decision Notes:'}
                        </div>
                        <div
                          className={`mt-0.5 break-words whitespace-pre-line ${detailTextClass}`}
                        >
                          {metadata.decisionNotes}
                        </div>
                      </MetaRow>
                    )}

                    {/* PENDING APPROVAL PERM SWAP: Vehicle Details */}
                    {metadata?.vehicleData && (
                      <>
                        <MetaRow
                          icon={
                            <i
                              className={`ri-truck-line text-xs ${iconClass}`}
                            />
                          }
                        >
                          <div className={detailMutedClass}>
                            Vehicle Details
                          </div>
                          <div
                            className={`mt-0.5 space-y-1 ${detailTextClass}`}
                          >
                            <div>
                              <span className={detailMutedClass}>
                                Tanggal & Waktu Kedatangan:
                              </span>{' '}
                              {formatDateTimeDDMMYYYYHHmm(
                                metadata.vehicleData.arriveAt
                              )}
                            </div>
                            <div>
                              <span className={detailMutedClass}>
                                Plate Number:
                              </span>{' '}
                              {metadata.vehicleData.platNomor || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>
                                Nomor Rangka/VIN:
                              </span>{' '}
                              {metadata.vehicleData.vin || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>
                                Nomor Mesin:
                              </span>{' '}
                              {metadata.vehicleData.imei || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>
                                OEM Name:
                              </span>{' '}
                              {metadata.vehicleData.oemName || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>
                                Model Name:
                              </span>{' '}
                              {metadata.vehicleData.modelName || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>Color:</span>{' '}
                              {metadata.vehicleData.color || '-'}
                            </div>
                            <div>
                              <span className={detailMutedClass}>Status:</span>{' '}
                              <span className="capitalize">
                                {metadata.vehicleData.status?.toLowerCase() ||
                                  '-'}
                              </span>
                            </div>
                            {metadata.vehicleData.stnkLink && (
                              <div>
                                <span className={detailMutedClass}>
                                  STNK Link:
                                </span>{' '}
                                <a
                                  href={metadata.vehicleData.stnkLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {metadata.vehicleData.stnkLink}
                                </a>
                              </div>
                            )}
                            {metadata.vehicleData.appUsername && (
                              <div>
                                <span className={detailMutedClass}>
                                  App Username:
                                </span>{' '}
                                {metadata.vehicleData.appUsername}
                              </div>
                            )}
                            {metadata.vehicleData.appPassword && (
                              <div>
                                <span className={detailMutedClass}>
                                  App Password:
                                </span>{' '}
                                {metadata.vehicleData.appPassword}
                              </div>
                            )}
                          </div>
                        </MetaRow>

                        {metadata.vehicleData.vehicleImageURL && (
                          <MetaRow
                            icon={
                              <i
                                className={`ri-image-line text-xs ${iconClass}`}
                              />
                            }
                          >
                            <div className={detailMutedClass}>Photo:</div>
                            <div
                              className={`mt-0.5 break-all ${detailTextClass}`}
                            >
                              <a
                                href={metadata.vehicleData.vehicleImageURL}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {metadata.vehicleData.vehicleImageURL}
                              </a>
                            </div>
                          </MetaRow>
                        )}

                        {metadata.vehicleData.unitEquipment &&
                          Array.isArray(metadata.vehicleData.unitEquipment) &&
                          metadata.vehicleData.unitEquipment.length > 0 && (
                            <MetaRow
                              icon={
                                <i
                                  className={`ri-list-check-2 text-xs ${iconClass}`}
                                />
                              }
                            >
                              <div className={detailMutedClass}>
                                Equipment Checklist:
                              </div>
                              <div className={`mt-0.5 ${detailTextClass}`}>
                                {metadata.vehicleData.unitEquipment.map(
                                  (item: string, idx: number) => (
                                    <div key={idx}>{item}</div>
                                  )
                                )}
                              </div>
                            </MetaRow>
                          )}

                        {metadata.vehicleData.notes && (
                          <MetaRow
                            icon={
                              <i
                                className={`ri-file-text-line text-xs ${iconClass}`}
                              />
                            }
                          >
                            <div className={detailMutedClass}>Notes:</div>
                            <div
                              className={`mt-0.5 break-words ${detailTextClass}`}
                            >
                              {metadata.vehicleData.notes}
                            </div>
                          </MetaRow>
                        )}
                      </>
                    )}

                    {/* PENDING APPROVAL PERM SWAP: Return Issues */}
                    {metadata?.draftReturnVehicle?.returnIssues && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-error-warning-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Return Issues:</div>
                        <div
                          className={`mt-0.5 break-words whitespace-pre-line ${detailTextClass}`}
                        >
                          {metadata.draftReturnVehicle.returnIssues}
                        </div>
                      </MetaRow>
                    )}

                    {/* PENDING APPROVAL PERM SWAP: Return Notes */}
                    {metadata?.draftReturnVehicle?.returnNotes && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-file-text-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Return Notes:</div>
                        <div
                          className={`mt-0.5 break-words ${detailTextClass}`}
                        >
                          {metadata.draftReturnVehicle.returnNotes}
                        </div>
                      </MetaRow>
                    )}
                    {/* PENDING APPROVAL TEMP SWAP: Vehicle info */}
                    {metadata?.tempPlateNumber && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-clipboard-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Swap Vehicles:</div>
                        <div className={`mt-0.5 space-y-1 ${detailTextClass}`}>
                          <div>
                            <span className="font-medium">
                              Temporary Plate Number:
                            </span>{' '}
                            {metadata.tempPlateNumber}
                          </div>
                          <div>
                            <span className="font-medium">OEM Name:</span>{' '}
                            {metadata.tempOemName || '-'}
                          </div>
                          <div>
                            <span className="font-medium">Model Name:</span>{' '}
                            {metadata.tempModelName || '-'}
                          </div>
                        </div>
                      </MetaRow>
                    )}

                    {/* PENDING APPROVAL TEMP SWAP: Given date */}
                    {metadata?.tempSwapGivenAt && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-calendar-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Swap Given Date:</div>
                        <div className={`mt-0.5 ${detailTextClass}`}>
                          {formatDateTimeDDMMYYYYHHmm(metadata.tempSwapGivenAt)}
                        </div>
                      </MetaRow>
                    )}

                    {/* PENDING APPROVAL TEMP SWAP: Return date */}
                    {metadata?.tempSwapReturnAt && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-calendar-check-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>
                          Return Date & Time:
                        </div>
                        <div className={`mt-0.5 ${detailTextClass}`}>
                          {formatDateTimeDDMMYYYYHHmm(
                            metadata.tempSwapReturnAt
                          )}
                        </div>
                      </MetaRow>
                    )}

                    {/* PENDING APPROVAL TEMP SWAP: Photo */}
                    {metadata?.tempSwapPhotos &&
                      Array.isArray(metadata.tempSwapPhotos) &&
                      metadata.tempSwapPhotos.length > 0 && (
                        <MetaRow
                          icon={
                            <i
                              className={`ri-image-line text-xs ${iconClass}`}
                            />
                          }
                        >
                          <div className={detailMutedClass}>Photo:</div>
                          <div
                            className={`mt-0.5 break-all ${detailTextClass}`}
                          >
                            {metadata.tempSwapPhotos.map((photoUrl, idx) => (
                              <div key={idx}>
                                <a
                                  href={photoUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {photoUrl}
                                </a>
                              </div>
                            ))}
                          </div>
                        </MetaRow>
                      )}

                    {/* PENDING APPROVAL TEMP SWAP: Notes */}
                    {metadata?.tempSwapNotes && (
                      <MetaRow
                        icon={
                          <i
                            className={`ri-file-text-line text-xs ${iconClass}`}
                          />
                        }
                      >
                        <div className={detailMutedClass}>Notes:</div>
                        <div
                          className={`mt-0.5 break-words ${detailTextClass}`}
                        >
                          {metadata.tempSwapNotes}
                        </div>
                      </MetaRow>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceStatusTimeline;
